import React, { type ReactNode, useCallback, useMemo } from 'react';
import { sub, startOfDay, formatISO } from 'date-fns';
import type { CustomThemeButtonProps } from '@atlaskit/button';
import { useShowCreateRetroOrCrossSellModal } from '@atlassian/jira-confluence-sprint-retros/src/controllers/show-create-retro-or-cross-sell-modal/index.tsx';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger';
import { ModalEntryPointContainer } from '@atlassian/jira-entry-point-modal-container';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import { createAri } from '@atlassian/jira-platform-ari';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import { completeSprintModalEntryPoint } from '@atlassian/jira-software-complete-sprint-modal-relay/entrypoint';
import { ModalTriggerButton } from '@atlassian/jira-software-modal-dialog/src/ui/modal-trigger-button/index.tsx';
import { BACKLOG } from '@atlassian/jira-software-resource-invalidator/src/common/types.tsx';
import { triggerResourceCacheInvalidation } from '@atlassian/jira-software-resource-invalidator/src/controllers/resources-cache-invalidation-subject/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { BACKLOG_PACKAGE_NAME } from '../../../common/constants/analytics';
import { REFRESH_SOURCE_SPRINT_COMPLETE } from '../../../model/constants';
import { completeSprintModalLoad } from '../../../services/utils/performance-analytics';
import { useBacklogDispatch, useBacklogSelector } from '../../../state';
import { refreshDataRequest } from '../../../state/actions/app';
import { getRapidViewId, getIsCompanyManaged } from '../../../state/selectors/app';
import { cardsSelector } from '../../../state/selectors/cards';

export interface Props {
	children: ReactNode;
	sprintId: string;
	testId: string;
	theme: CustomThemeButtonProps['theme'];
}

const CompleteSprintButton = ({ children, sprintId, testId, theme }: Props) => {
	const boardId = useBacklogSelector(getRapidViewId);
	const cloudId = useCloudId();
	const { data: project } = useProjectContext();
	const jiraProjectAri = createAri({
		resourceOwner: 'jira',
		cloudId,
		resourceType: 'project',
		resourceId: String(project?.projectId),
	});
	const sourceIssueNo = useBacklogSelector(cardsSelector).length;
	const dispatch = useBacklogDispatch();

	const boardIdAri = createAri({
		cloudId,
		resourceId: String(boardId),
		resourceOwner: 'jira-software',
		resourceType: 'board',
	});

	const sprintIdAri = createAri({
		cloudId,
		resourceId: sprintId,
		resourceOwner: 'jira-software',
		resourceType: 'sprint',
	});

	const isCompanyManaged = useBacklogSelector(getIsCompanyManaged);

	const showRetrosModal = useShowCreateRetroOrCrossSellModal();

	const onCompleted = useCallback(() => {
		dispatch(refreshDataRequest(REFRESH_SOURCE_SPRINT_COMPLETE));
		triggerResourceCacheInvalidation(BACKLOG, 'COMPLETE_SPRINT_MODAL_COMPLETE');
		showRetrosModal();
	}, [dispatch, showRetrosModal]);

	const entryPointParams = useMemo(
		() => ({
			jiraProjectAri,
			// Date of 12 weeks ago as we want to fetch deployments date updated at most 12 weeks ago for deployments report nudge on sprint completion
			updatedFromDeploymentsDate: formatISO(startOfDay(sub(Date.now(), { weeks: 12 }))),
			boardId: boardIdAri,
			activeSprintIds: [sprintIdAri],
			isCompanyManaged,
		}),
		[jiraProjectAri, boardIdAri, sprintIdAri, isCompanyManaged],
	);

	const { entryPointActions, entryPointReferenceSubject, stopMetric } = useEntryPoint(
		completeSprintModalEntryPoint,
		entryPointParams,
		completeSprintModalLoad,
	);

	const entryPointTriggerRef = useEntryPointButtonTrigger(entryPointActions);

	const confluenceRetrosAvailable = ff('sprint-retros-company-managed') ? true : !isCompanyManaged;

	const props = useMemo(
		() => ({
			analyticsSource: 'software-backlog',
			boardId: boardIdAri,
			onCompleted,
			sourceIssueNo,
			sprintId,
			onReady: stopMetric,
			confluenceRetrosAvailable,
			isCompanyManaged,
		}),
		[
			boardIdAri,
			onCompleted,
			sourceIssueNo,
			sprintId,
			stopMetric,
			isCompanyManaged,
			confluenceRetrosAvailable,
		],
	);

	return (
		<>
			<ModalTriggerButton ref={entryPointTriggerRef} theme={theme} testId={testId}>
				{children}
			</ModalTriggerButton>

			<ModalEntryPointContainer
				entryPointReferenceSubject={entryPointReferenceSubject}
				id="complete-sprint-modal"
				teamName="a4t-pandora"
				packageName={BACKLOG_PACKAGE_NAME}
				entryPointActions={entryPointActions}
				runtimeProps={props}
			/>
		</>
	);
};

export default CompleteSprintButton;
